import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
} from '@mui/material';
import {
  Healing,
  Sports,
  PregnantWoman,
  Psychology,
  LocalHospital,
  FitnessCenter,
  Spa,
  AccessibilityNew,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionCard = motion(Card);

const Services: React.FC = () => {
  const navigate = useNavigate();
  const services = [
    {
      icon: <Healing />,
      title: 'Back Pain Relief',
      description: 'Expert treatment for chronic and acute back pain using evidence-based techniques.',
      features: ['Spinal Manipulation', 'Soft Tissue Therapy', 'Exercise Prescription'],
      color: '#e8f5e8',
    },
    {
      icon: <Psychology />,
      title: 'Neck Pain & Headaches',
      description: 'Comprehensive care for neck pain, tension headaches, and cervical spine issues.',
      features: ['Cervical Adjustment', 'Trigger Point Therapy', 'Posture Correction'],
      color: '#f1f8e9',
    },
    {
      icon: <Sports />,
      title: 'Sports Injuries',
      description: 'Specialized treatment for athletes and sports-related injuries.',
      features: ['Injury Assessment', 'Rehabilitation', 'Performance Optimization'],
      color: '#e8f5e8',
    },
    {
      icon: <FitnessCenter />,
      title: 'Return to Sport/Rehabilitation',
      description: 'Comprehensive rehabilitation programs to safely return athletes to their sport.',
      features: ['Functional Assessment', 'Progressive Training', 'Sport-Specific Rehab'],
      color: '#f1f8e9',
    },
    {
      icon: <FitnessCenter />,
      title: 'Workplace Injuries',
      description: 'Treatment and prevention of work-related musculoskeletal disorders.',
      features: ['Ergonomic Assessment', 'WorkCover Claims', 'Return to Work Programs'],
      color: '#f1f8e9',
    },
    {
      icon: <LocalHospital />,
      title: 'Disc Bulges & Sciatica',
      description: 'Specialized care for disc problems and sciatic nerve pain.',
      features: ['Spinal Decompression', 'Neural Mobilization', 'Core Strengthening'],
      color: '#e8f5e8',
    },
    {
      icon: <PregnantWoman />,
      title: 'Pregnancy Care',
      description: 'Safe and gentle chiropractic care during pregnancy and postpartum.',
      features: ['Prenatal Adjustments', 'Pelvic Balance', 'Labor Preparation'],
      color: '#f1f8e9',
    },
    {
      icon: <AccessibilityNew />,
      title: 'Vertigo & Balance',
      description: 'Treatment for dizziness, vertigo, and balance disorders.',
      features: ['Vestibular Rehabilitation', 'Balance Training', 'Epley Maneuvers'],
      color: '#f1f8e9',
    },
    {
      icon: <Spa />,
      title: 'Myotherapy & Remedial Massage',
      description: 'Comprehensive myotherapy and remedial massage therapy for musculoskeletal pain relief and functional improvement.',
      features: ['Myofascial Release', 'Trigger Point Therapy', 'Deep Tissue Massage'],
      color: '#e8f5e8',
    },
  ];

  return (
    <Box
      id="services"
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: '#fafafa',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: { xs: 2, md: 2 },
              background: 'linear-gradient(45deg, #2c3e50, #8bc34a)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
              px: { xs: 2, md: 0 },
            }}
          >
            Our Services
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: { xs: 4, md: 6 },
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
              fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
              px: { xs: 3, md: 0 },
              lineHeight: { xs: 1.6, md: 1.6 },
            }}
          >
            Comprehensive chiropractic care tailored to your specific needs. 
            We combine traditional techniques with modern evidence-based treatments.
          </Typography>
        </motion.div>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <MotionCard
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: { xs: '12px', md: '16px' },
                  border: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: { xs: 'none', md: 'translateY(-8px)' },
                    boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.1)', md: '0 8px 30px rgba(0,0,0,0.15)' },
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: { xs: 2.5, md: 3 } }}>
                  <Box
                    sx={{
                      width: { xs: 50, md: 60 },
                      height: { xs: 50, md: 60 },
                      borderRadius: { xs: '10px', md: '12px' },
                      backgroundColor: service.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 1.5, md: 2 },
                      color: 'primary.main',
                    }}
                  >
                    {React.cloneElement(service.icon, { sx: { fontSize: { xs: 24, md: 30 } } })}
                  </Box>

                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: { xs: 1.5, md: 2 }, 
                      fontWeight: 600,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                    }}
                  >
                    {service.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ 
                      mb: { xs: 2.5, md: 3 }, 
                      color: 'text.secondary', 
                      lineHeight: 1.6,
                      fontSize: { xs: '0.875rem', md: '0.875rem' },
                    }}
                  >
                    {service.description}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, md: 1 } }}>
                    {service.features.map((feature, featureIndex) => (
                      <Chip
                        key={featureIndex}
                        label={feature}
                        size="small"
                        sx={{
                          backgroundColor: service.color,
                          color: 'primary.main',
                          fontWeight: 500,
                          fontSize: { xs: '0.7rem', md: '0.75rem' },
                          height: { xs: '24px', md: '28px' },
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>

                <CardActions sx={{ p: { xs: 2.5, md: 3 }, pt: 0 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: '#8bc34a',
                      color: '#2c3e50',
                      fontSize: { xs: '0.875rem', md: '0.875rem' },
                      py: { xs: 1, md: 1.2 },
                      borderWidth: 2,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#8bc34a',
                        borderColor: '#8bc34a',
                        color: 'white',
                      },
                    }}
                    onClick={() => navigate('/bookings')}
                  >
                    Book Consultation
                  </Button>
                </CardActions>
              </MotionCard>
            </Grid>
          ))}
        </Grid>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              mt: { xs: 6, md: 8 },
              p: { xs: 3, md: 4 },
              backgroundColor: 'white',
              borderRadius: { xs: '12px', md: '16px' },
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              mx: { xs: 1, md: 0 },
            }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                mb: { xs: 1.5, md: 2 }, 
                fontWeight: 600,
                fontSize: { xs: '1.3rem', md: '1.5rem' },
                px: { xs: 1, md: 0 },
              }}
            >
              Why Choose Our Clinic?
            </Typography>
            <Grid container spacing={{ xs: 2.5, md: 3 }} sx={{ mt: { xs: 1, md: 2 } }}>
              <Grid item xs={12} md={4}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'primary.main', 
                    mb: { xs: 0.5, md: 1 },
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                  }}
                >
                  Evidence-Based
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: { xs: '0.875rem', md: '0.875rem' },
                    lineHeight: 1.5,
                    px: { xs: 1, md: 0 },
                  }}
                >
                  All treatments based on latest research and proven techniques
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'primary.main', 
                    mb: { xs: 0.5, md: 1 },
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                  }}
                >
                  Multimodal Approach
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: { xs: '0.875rem', md: '0.875rem' },
                    lineHeight: 1.5,
                    px: { xs: 1, md: 0 },
                  }}
                >
                  Combining chiropractic care with massage and exercise therapy
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'primary.main', 
                    mb: { xs: 0.5, md: 1 },
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                  }}
                >
                  Personalized Care
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: { xs: '0.875rem', md: '0.875rem' },
                    lineHeight: 1.5,
                    px: { xs: 1, md: 0 },
                  }}
                >
                  Treatment plans tailored to your specific needs and goals
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Services;